import type { ReactNode } from "react";
import type { SnapshotPayload } from "@shared/types.js";
import { fstamp } from "../lib/format.js";

const MAP_ROWS: string[][] = [
  ["Accommodation", "Accommodation", "Supplier Code", "Category", "FIT - CR Requested Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Accommodation (Cat. E sheet)", "Cat. E", "Supplier Code", "Category", "FIT - CR Requested Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Activities", "Activity", "Product Code", "Category", "CR Receive Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Entrance Fee", "Entrance Fee", "Supplier Code", "Category", "CR Receive Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Transportation", "Transport", "Supplier Code", "Category", "CR Receive Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Guide Fee", "Guide", "Supplier Code + Product", "Category (empty)", "CR Receive Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
  ["Train", "Train", "Supplier Code", "Category", "CR Receive Status",
    "New Rate Input Status", "Estimation Price Input Status", "Crosscheck Date", "Export to Extranet Date"],
];

function Flag({ crit, title, children }: { crit?: boolean; title: string; children: ReactNode }) {
  return (
    <div className={`callout${crit ? " crit" : ""}`}>
      <span className="ic" />
      <div>
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </div>
  );
}

export function Mapping({ snap }: { snap: SnapshotPayload }) {
  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Reference</span>
          <h2><i className="bi bi-diagram-3 hico" aria-hidden="true" />Field mapping &amp; definitions</h2>
          <span className="note">How each number on this page is derived from the Masterfile</span>
        </div>

        <div className="card card-pad">
          <h3 style={{ fontSize: 13, marginBottom: 9 }}>Status definitions</h3>
          <div className="defs">
            <div><b>Received</b><span>CR status column reads exactly <code>Received New Rate</code>.</span></div>
            <div><b>Not received</b><span>Anything else, including blank &mdash; this is the estimation pool.</span></div>
            <div><b>Priced / entered</b><span><code>New Rate Input Status = Done</code>.</span></div>
            <div><b>Estimated</b><span><code>Estimation Price Input Status = Done</code>.</span></div>
            <div><b>Outstanding</b><span>Received but not priced, or not received and not estimated.</span></div>
            <div><b>Crosschecked</b><span><code>Crosscheck Date</code> is filled.</span></div>
            <div><b>Exported</b><span><code>Export to Extranet Date</code> is filled.</span></div>
            <div><b>Percentage</b><span>Relevant records &divide; total applicable records &times; 100, computed live.</span></div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-head">
            <h3><i className="bi bi-columns-gap hico" aria-hidden="true" />Column mapping per sheet</h3>
            <span className="sub">
              Taken from the sheet headers, cross-checked against the Overview tab&rsquo;s own formulas
            </span>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Component</th><th>Sheet</th><th>Unique key</th><th>Category</th>
                  <th>CR received</th><th>New rate status</th><th>Estimation status</th>
                  <th>Crosscheck</th><th>Export</th>
                </tr>
              </thead>
              <tbody>
                {MAP_ROWS.map((r) => (
                  <tr key={r[1]}>
                    <td className="strong">{r[0]}</td>
                    {r.slice(1).map((v, i) => (
                      <td key={i}><code style={{ fontSize: 11 }}>{v}</code></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-flag hico" aria-hidden="true" />Flagged for your decision</h2>
          <span className="note">Ambiguities in the Masterfile &mdash; surfaced, not guessed at</span>
        </div>
        <div className="grid" style={{ gap: 10 }}>
          <Flag crit title="The Cat. E sheet is not counted by default">
            The workbook holds 217 category-E accommodation rows on a separate <b>Cat. E</b> sheet,
            and only 3 of those supplier codes also appear on the main Accommodation sheet. The
            Overview tab&rsquo;s formulas read the Accommodation sheet only, so this dashboard
            follows that convention by default and excludes them. Use the <b>Cat. E sheet</b>{" "}
            toggle in the filter bar to include them &mdash; headline totals go from 603 to 820
            records. Decide which is right: if Cat. E holds live suppliers, it belongs in the
            count, and the 3 overlapping codes need resolving first.
          </Flag>

          <Flag title={"“Details…” sheets duplicate four components"}>
            <b>Details Activity</b>, <b>Details Transport</b>, <b>Detail Entrance Fee</b> and{" "}
            <b>Detail Trains</b> mirror sheets that are already counted. Three are byte-identical
            copies. <b>Details Activity</b> is not: it holds 81 rows against Activity&rsquo;s 68,
            with 15 marked priced against Activity&rsquo;s 4. Only the counted sheet feeds this
            dashboard; the reconciliation table on the Crosscheck tab shows the gap.
          </Flag>

          <Flag title="Guide Fee has no category values">
            All 28 Guide rows leave <code>Category</code> blank, so Guide Fee cannot be broken
            down A&ndash;E. Guide also reuses one supplier code (858, Happy Trails Indonesia)
            across all 28 products, so the unique key there is supplier code + product, not code alone.
          </Flag>

          <Flag title={"The Overview tab’s Guide and Export figures read differently here"}>
            Two formula quirks in the workbook: Guide Fee shows <code>28 / 100%</code> estimated
            because <code>COUNTIF(&lt;&gt;&quot;Received New Rate&quot;)</code> counts blank rows
            as matches; and the &ldquo;Export to Extranet&rdquo; column actually counts the{" "}
            <code>Upload Date</code> column, not <code>Export to Extranet Date</code> (149 vs 144
            for Accommodation). This dashboard counts only explicit <code>Done</code> values and
            reads the export column by its own name.
          </Flag>

          <Flag title="Spelling variants in the source data">
            <code>Sumatra</code> and <code>Sumatera</code> both appear as regions, and{" "}
            <code>New Rate</code> / <code>new rate</code> both appear as rate types. They are
            listed separately in the filters because that is what the file says &mdash; worth
            normalising at source.
          </Flag>

          <Flag title={"Everything reproduces the workbook’s own Overview tab"}>
            Where the two can be compared, the figures here match the Overview tab exactly:
            Accommodation 473 records, 107 received, 87 priced, 366 not received, 77 estimated,
            144 crosschecked; Activities 68 / 3 / 4 / 65 / 0 / 4; and every A&ndash;E category
            split. The differences above are the deliberate ones, each listed here.
          </Flag>
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 14 }}>
        <h3 style={{ fontSize: 13 }}>Keeping this dashboard current</h3>
        <p style={{ margin: "5px 0 0", fontSize: 12, color: "var(--ink-2)" }}>
          Press <b>Upload Masterfile</b> in the header and pick an updated <code>.xlsx</code>.
          The file is parsed on the server using the mapping above and stored as a new{" "}
          <b>snapshot</b>, so every upload is kept and the <b>Progress</b> tab can chart movement
          between them. Sheet and column names must stay as they are; if a sheet is renamed the
          upload will report which one it could not find.
        </p>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--ink-2)" }}>
          This snapshot: <b>{snap.meta.sourceFile}</b>, parsed {fstamp(snap.meta.generatedAt)},{" "}
          {snap.records.length} records from {snap.sheetsInWorkbook.length} sheets in the workbook.
        </p>
        {snap.notes.length > 0 && (
          <div className="err" style={{ marginTop: 10 }}>
            <b>Parser notes:</b>
            <ul style={{ margin: "5px 0 0", paddingLeft: 18 }}>
              {snap.notes.map((nte, i) => <li key={i}>{nte}</li>)}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
